import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Combobox, Transition } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

import { TbSelector } from 'react-icons/tb';

import type { EmptyStateProps } from 'components-new/empty-state/empty-state';
import Checkbox from 'components-new/checkbox/checkbox';
import Chip from 'components-new/chip/chip';
import EmptyState from 'components-new/empty-state/empty-state';
import Icon from 'components-new/icon/icon';
import Typography, {
  TypographyVariantStyles,
} from 'components-new/typography/typography';

export type AsyncSelectTagOption = {
  id: string | number;
  value: string;
  disabled?: boolean;
  active?: boolean;
};

export type AsyncSelectTagsProps = {
  placeholder?: string;
  className?: string;
  options: Array<AsyncSelectTagOption>;
  loading?: boolean;
  disabled?: boolean;
  error?: string;
  onSelectedValueChange?: (value: Array<number>) => void;
  onQueryChange?: (query: string | null) => void;
  query: string | null;
  defaultSelectedValues?: Array<{ id: number; value: string }>;
  emptyStateProps?: EmptyStateProps;
};

const AsyncSelectTags: React.FC<AsyncSelectTagsProps> = (props) => {
  const {
    options,
    loading,
    disabled,
    error,
    onQueryChange,
    onSelectedValueChange,
    defaultSelectedValues,
    emptyStateProps,
    className,
  } = props;
  const [query, setQuery] = useState<string | null>(props.query);
  const [selectedValue, setSelectedValue] = useState<
    Array<AsyncSelectTagOption>
  >([]);

  const emptyResult = useMemo(
    () => options.length === 0 && query !== '' && !loading,
    [query, options, loading],
  );

  const onChange = useCallback(
    (values: Array<AsyncSelectTagOption>) => {
      // Any redundant value should be removed
      const filtered = values.filter(
        (v) => values.filter((item) => item.id === v.id).length === 1,
      );
      setSelectedValue(filtered);
      setQuery(null);
      onSelectedValueChange?.(filtered.map((v) => v.id) as Array<number>);
    },
    [onSelectedValueChange, setSelectedValue],
  );

  const onChipCloseCliked = (chipId?: string) => {
    const newSelectedValue = selectedValue.filter((v) => v.id !== chipId);
    setSelectedValue(newSelectedValue);
    props.onSelectedValueChange?.(
      newSelectedValue?.map((v) => v.id) as Array<number>,
    );
  };

  useEffect(() => {
    onQueryChange?.(query);
  }, [onQueryChange, query]);

  useEffect(() => {
    if (!props.defaultSelectedValues) return;

    setSelectedValue(
      props.defaultSelectedValues.map((s) => ({
        active: true,
        id: s.id,
        value: s.value,
      })),
    );
  }, [props.defaultSelectedValues]);

  const containerClassName = twMerge(
    'relative bg-transparent rounded',
    className,
  );

  const inputClassName = twMerge(
    'relative h-[34px] text-start cursor-default',
    'rounded-md bg-input-idle w-full px-3',
    'transition ease-in-out duration-200',
    'border focus-visible:outline-none',
    !disabled && 'hover:bg-input-hover focus-within:bg-input-active',
    !disabled &&
      !error &&
      `border-transparent
       active:border-primary active:outline active:outline-primary-200 active:input-idle active:bg-input-active
       focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1 focus:ring-offset-primary-200
       focus-within:ring-2 focus-within:ring-primary-200
       focus:border-primary`,
    error &&
      'border border-destructive-300 focus-within:ring-2 focus-within:ring-destructive-100',
    TypographyVariantStyles['ui-input'],
  );

  return (
    <div className={containerClassName}>
      <Combobox value={selectedValue} onChange={onChange} nullable multiple>
        <div className="relative">
          {defaultSelectedValues && defaultSelectedValues.length !== 0 && (
            <div className="flex flex-wrap gap-x-2 gap-y-2  p-2 ">
              {defaultSelectedValues.map((s) => (
                <Chip
                  key={s.id}
                  id={s.id as unknown as string}
                  title={s.value}
                  className="inline-flex"
                  onClose={onChipCloseCliked}
                />
              ))}
            </div>
          )}
          <div className="flex items-center">
            <Combobox.Input
              className={inputClassName}
              placeholder={props.placeholder || 'Search'}
              onChange={(event) => setQuery(event.target.value)}
              autoComplete="false"
              displayValue={(item: AsyncSelectTagOption) =>
                item?.value ?? query
              }
            />
            <Combobox.Button className="absolute right-0 flex items-center pr-2">
              <Icon
                icon={TbSelector}
                className={!disabled ? 'text-gray-400' : 'text-gray-200'}
              />
            </Combobox.Button>
          </div>
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Combobox.Options className="absolute mt-1 z-10 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-gray100 ring-opacity-5 border border-gray100 focus:outline-none">
            {emptyResult && <EmptyState {...emptyStateProps} />}
            {options.map((item) => (
              <Combobox.Option
                key={item.id}
                value={item}
                className={twMerge(
                  'relative cursor-default select-none py-2 hover:bg-background w-full flex gap-x-2 items-center pr-4',
                )}
              >
                <span className="flex items-center pl-3">
                  {item.active ? (
                    <Checkbox variant="checked" />
                  ) : (
                    <Checkbox variant="unchecked" />
                  )}
                </span>
                <Typography variant="body-medium" as="p">
                  {item.value}
                </Typography>
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Transition>
      </Combobox>
    </div>
  );
};

export default AsyncSelectTags;
